import _defineProperty from "/home/runner/work/3/3/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/home/runner/work/3/3/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/home/runner/work/3/3/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as React from "react";
import { createModule } from "../config.js";
import { cleanup, clsx, cssClass, cssVar, isNumber, makeComposePrefix, makeUseContext, parseLengthPercentage } from "../utils.js";
import { useContainerRect, useEventCallback, useForkRef, useLayoutEffect, useMotionPreference, useRTL, useSensors } from "../hooks/index.js";
import { useEvents, useLightboxState, useTimeouts } from "../contexts/index.js";
import { SwipeState, usePointerSwipe, usePreventSwipeNavigation, useWheelSwipe } from "./controller/index.js";
import { ACTION_CLOSE, ACTION_NEXT, ACTION_PREV, CLASS_FLEX_CENTER, EVENT_ON_KEY_UP, MODULE_CONTROLLER, VK_ESCAPE, YARL_EVENT_BACKDROP_CLICK } from "../consts.js";
var cssContainerPrefix = makeComposePrefix("container");
var ControllerContext = React.createContext(null);
export var useController = makeUseContext("useController", "ControllerContext", ControllerContext);
export var Controller = function Controller(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);

  var carousel = props.carousel,
      slides = props.slides,
      animation = props.animation,
      controller = props.controller,
      on = props.on,
      styles = props.styles;

  var _useLightboxState = useLightboxState(),
      state = _useLightboxState.state,
      dispatch = _useLightboxState.dispatch;

  var _React$useState = React.useState(SwipeState.NONE),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      swipeState = _React$useState2[0],
      setSwipeState = _React$useState2[1];

  var swipeOffset = React.useRef(0);
  var swipeAnimationReset = React.useRef();

  var _useSensors = useSensors(),
      registerSensors = _useSensors.registerSensors,
      subscribeSensors = _useSensors.subscribeSensors;

  var _useEvents = useEvents(),
      subscribe = _useEvents.subscribe,
      publish = _useEvents.publish;

  var _useTimeouts = useTimeouts(),
      setTimeout = _useTimeouts.setTimeout,
      clearTimeout = _useTimeouts.clearTimeout;

  var _useContainerRect = useContainerRect(),
      containerRef = _useContainerRect.containerRef,
      setContainerRef = _useContainerRect.setContainerRef,
      containerRect = _useContainerRect.containerRect;

  var handleContainerRef = useForkRef(usePreventSwipeNavigation(), setContainerRef);
  var carouselRef = React.useRef(null);
  var setCarouselRef = useForkRef(carouselRef, undefined);
  var carouselAnimation = React.useRef();
  var carouselSwipeAnimation = React.useRef();
  var reduceMotion = useMotionPreference();
  var isRTL = useRTL();
  var rtl = useEventCallback(function (value) {
    return (isRTL ? -1 : 1) * (isNumber(value) ? value : 1);
  });
  var isSwipeValid = useEventCallback(function (offset) {
    return !(carousel.finite && (rtl(offset) > 0 && state.currentIndex === 0 || rtl(offset) < 0 && state.currentIndex === slides.length - 1));
  });
  var setSwipeOffset = React.useCallback(function (offset) {
    var _a;

    swipeOffset.current = offset;
    (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(cssVar("swipe_offset"), "".concat(Math.round(offset), "px"));
  }, [containerRef]);
  var swipe = useEventCallback(function (action) {
    var _a;

    var swipeDuration = animation.swipe;
    var currentSwipeOffset = action.offset || 0;
    var direction = action.direction;
    var count = (_a = action.count) !== null && _a !== void 0 ? _a : 1;
    var newSwipeState = SwipeState.ANIMATION;
    var newSwipeAnimationDuration = swipeDuration * count;

    if (!direction) {
      var containerWidth = containerRect === null || containerRect === void 0 ? void 0 : containerRect.width;
      var elapsedTime = action.duration || 0;
      var expectedTime = containerWidth ? swipeDuration / containerWidth * Math.abs(currentSwipeOffset) : swipeDuration;

      if (count !== 0) {
        if (elapsedTime < expectedTime) {
          newSwipeAnimationDuration = newSwipeAnimationDuration / expectedTime * Math.max(elapsedTime, expectedTime / 5);
        } else if (containerWidth) {
          newSwipeAnimationDuration = swipeDuration / containerWidth * (containerWidth - Math.abs(currentSwipeOffset));
        }
      } else {
        newSwipeAnimationDuration = swipeDuration / 2;
      }

      if (count !== 0) {
        direction = rtl(currentSwipeOffset) > 0 ? ACTION_PREV : ACTION_NEXT;
      }
    }

    var increment;

    if (direction === ACTION_PREV) {
      if (isSwipeValid(rtl(1))) {
        increment = -count;
      } else {
        newSwipeState = SwipeState.NONE;
        newSwipeAnimationDuration = swipeDuration;
      }
    } else if (direction === ACTION_NEXT) {
      if (isSwipeValid(rtl(-1))) {
        increment = count;
      } else {
        newSwipeState = SwipeState.NONE;
        newSwipeAnimationDuration = swipeDuration;
      }
    }

    if (carouselRef.current) {
      carouselSwipeAnimation.current = {
        rect: carouselRef.current.getBoundingClientRect(),
        index: state.globalIndex
      };
    }

    newSwipeAnimationDuration = Math.round(newSwipeAnimationDuration);
    clearTimeout(swipeAnimationReset.current);

    if (newSwipeState) {
      var timeoutId = setTimeout(function () {
        if (swipeAnimationReset.current === timeoutId) {
          setSwipeOffset(0);
          setSwipeState(SwipeState.NONE);
        }
      }, newSwipeAnimationDuration);
      swipeAnimationReset.current = timeoutId;
    }

    setSwipeState(newSwipeState);
    dispatch({
      increment: increment,
      animationDuration: newSwipeAnimationDuration
    });
  });
  var animateCarouselSwipe = useEventCallback(function () {
    var _a, _b, _c;

    var swipeAnimation = carouselSwipeAnimation.current;
    carouselSwipeAnimation.current = undefined;

    if (swipeAnimation && carouselRef.current && containerRect) {
      var parsedSpacing = parseLengthPercentage(carousel.spacing);
      var spacingValue = (parsedSpacing.percent ? parsedSpacing.percent * containerRect.width / 100 : parsedSpacing.pixel) || 0;
      (_a = carouselAnimation.current) === null || _a === void 0 ? void 0 : _a.cancel();
      carouselAnimation.current = (_c = (_b = carouselRef.current).animate) === null || _c === void 0 ? void 0 : _c.call(_b, [{
        transform: "translateX(".concat(rtl(state.globalIndex - swipeAnimation.index) * (containerRect.width + spacingValue) + swipeAnimation.rect.x - carouselRef.current.getBoundingClientRect().x, "px)")
      }, {
        transform: "translateX(0)"
      }], !reduceMotion ? state.animationDuration : 0);

      if (carouselAnimation.current) {
        carouselAnimation.current.onfinish = function () {
          carouselAnimation.current = undefined;
        };
      }
    }
  });
  useLayoutEffect(animateCarouselSwipe);
  var swipeParams = [subscribeSensors, isSwipeValid, (containerRect === null || containerRect === void 0 ? void 0 : containerRect.width) || 0, animation.swipe, function () {
    return setSwipeState(SwipeState.SWIPE);
  }, function (offset) {
    return setSwipeOffset(offset);
  }, function (offset, duration) {
    return swipe({
      offset: offset,
      duration: duration,
      count: 1
    });
  }, function (offset) {
    return swipe({
      offset: offset,
      count: 0
    });
  }];
  usePointerSwipe.apply(void 0, swipeParams);
  useWheelSwipe.apply(void 0, [swipeState].concat(swipeParams));
  var focusOnMount = useEventCallback(function () {
    var _a;

    if (controller.focus) {
      (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }
  });
  React.useEffect(focusOnMount, [focusOnMount]);
  var handleIndexChange = useEventCallback(function () {
    var _a;

    (_a = on.view) === null || _a === void 0 ? void 0 : _a.call(on, state.currentIndex);
  });
  React.useEffect(handleIndexChange, [state.currentIndex, handleIndexChange]);
  React.useEffect(function () {
    return cleanup(subscribe(ACTION_PREV, function (count) {
      return swipe({
        direction: ACTION_PREV,
        count: isNumber(count) ? count : undefined
      });
    }), subscribe(ACTION_NEXT, function (count) {
      return swipe({
        direction: ACTION_NEXT,
        count: isNumber(count) ? count : undefined
      });
    }));
  }, [subscribe, swipe]);
  React.useEffect(function () {
    return subscribeSensors(EVENT_ON_KEY_UP, function (event) {
      if (event.code === VK_ESCAPE) {
        publish(ACTION_CLOSE);
      }
    });
  }, [subscribeSensors, publish]);
  React.useEffect(function () {
    return controller.closeOnBackdropClick ? subscribe(YARL_EVENT_BACKDROP_CLICK, function () {
      return publish(ACTION_CLOSE);
    }) : function () {};
  }, [controller.closeOnBackdropClick, publish, subscribe]);
  var transferFocus = useEventCallback(function () {
    var _a;

    return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  });
  var getLightboxProps = useEventCallback(function () {
    return props;
  });
  var context = React.useMemo(function () {
    return {
      getLightboxProps: getLightboxProps,
      subscribeSensors: subscribeSensors,
      transferFocus: transferFocus,
      containerRect: containerRect || {
        width: 0,
        height: 0
      },
      containerRef: containerRef,
      setCarouselRef: setCarouselRef
    };
  }, [getLightboxProps, subscribeSensors, transferFocus, containerRect, containerRef, setCarouselRef]);
  return React.createElement("div", _objectSpread(_objectSpread({
    ref: handleContainerRef,
    className: clsx(cssClass(cssContainerPrefix()), cssClass(CLASS_FLEX_CENTER)),
    style: _objectSpread(_objectSpread(_objectSpread({}, swipeState === SwipeState.SWIPE ? _defineProperty({}, cssVar("swipe_offset"), "".concat(Math.round(swipeOffset.current), "px")) : null), controller.touchAction !== "none" ? _defineProperty({}, cssVar("controller_touch_action"), controller.touchAction) : null), styles.container)
  }, controller.aria ? {
    role: "presentation",
    "aria-live": "polite"
  } : null), {}, {
    tabIndex: -1
  }, registerSensors), containerRect && React.createElement(ControllerContext.Provider, {
    value: context
  }, children));
};
export var ControllerModule = createModule(MODULE_CONTROLLER, Controller);